import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Send, Search, Loader2, XCircle, Building2, Trash2 } from 'lucide-react';
import SEO from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import { messageService, Conversation, Message } from '../services/messageService';
import { schoolService, School } from '../services/schoolService';
import { useSearchParams } from 'react-router-dom';
import HeroSection from '../components/HeroSection';
import heroData from '../data/heroData.json';

const Messages = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [schoolId, setSchoolId] = useState<string | null>(searchParams.get('schoolId'));
  const [schoolDetails, setSchoolDetails] = useState<School | null>(null);
  const shouldScrollToBottom = useRef(false);
  const previousMessagesLength = useRef(0);
  const isInitialLoad = useRef(true);

  useEffect(() => {
    fetchConversations();
    
    // If schoolId is in URL on initial load, fetch messages immediately
    const urlSchoolId = searchParams.get('schoolId');
    if (urlSchoolId && !selectedConversation) {
      // Set initial load flag so it scrolls to bottom when messages load
      isInitialLoad.current = true;
      fetchMessages(undefined, urlSchoolId);
      // Don't fetch school details here - we'll get it from the conversation when it loads
    }
  }, []);

  useEffect(() => {
    // Always use schoolId to fetch messages to ensure we get all messages
    // This works better than conversationId which might miss some messages
    if (selectedConversation?.school?._id) {
      // Reset initial load flag so it scrolls to bottom when messages load
      isInitialLoad.current = true;
      fetchMessages(undefined, selectedConversation.school._id);
      if (selectedConversation.school.slug) {
        fetchSchoolDetails(selectedConversation.school.slug);
      }
    } else if (schoolId) {
      // Reset initial load flag so it scrolls to bottom when messages load
      isInitialLoad.current = true;
      fetchMessages(undefined, schoolId);
      // Find the conversation for this school to get the slug
      const conversation = conversations.find(c => c.school?._id === schoolId);
      if (conversation?.school?.slug) {
        fetchSchoolDetails(conversation.school.slug);
      }
    }
  }, [selectedConversation, schoolId, conversations]);

  const fetchSchoolDetails = async (idOrSlug: string) => {
    try {
      const response = await schoolService.getSchoolById(idOrSlug);
      if (response.success && response.data && response.data.school) {
        setSchoolDetails(response.data.school);
      }
    } catch (err) {
      // If fetching fails, it's okay - we'll just use the school name from conversation
      // Don't show error to user, just log it
      console.error('Failed to fetch school details:', err);
    }
  };

  const messagesContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Scroll to bottom if:
    // 1. A new message was sent (shouldScrollToBottom flag is set)
    // 2. Initial load - scroll to bottom when messages are first loaded
    // 3. New message received - scroll if user is near bottom
    if (shouldScrollToBottom.current) {
      scrollToBottom();
      shouldScrollToBottom.current = false;
    } else if (messages.length > 0 && !loadingMessages) {
      if (isInitialLoad.current) {
        // Scroll to bottom on initial load
        scrollToBottom();
        isInitialLoad.current = false;
      } else if (messages.length > previousMessagesLength.current) {
        // New message received - check if user is near bottom before auto-scrolling
        const messagesContainer = messagesContainerRef.current;
        if (messagesContainer) {
          const isNearBottom = messagesContainer.scrollHeight - messagesContainer.scrollTop - messagesContainer.clientHeight < 200;
          if (isNearBottom) {
            scrollToBottom();
          }
        }
      }
    }
    previousMessagesLength.current = messages.length;
  }, [messages, loadingMessages]);

  const scrollToBottom = () => {
    setTimeout(() => {
      const messagesContainer = messagesContainerRef.current;
      if (messagesContainer) {
        // Scroll the messages container, not the page
        messagesContainer.scrollTo({
          top: messagesContainer.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 150);
  };

  const fetchConversations = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await messageService.getConversations();
      setConversations(response.data.conversations);
      
      // If schoolId is in URL, try to find existing conversation
      // But don't set selectedConversation - we'll use schoolId directly to fetch messages
      // This ensures we get all messages for that school
      const urlSchoolId = searchParams.get('schoolId');
      if (urlSchoolId && !selectedConversation) {
        // Don't auto-select conversation - let the schoolId-based fetch handle it
        // This ensures we get all messages, not just those in a specific conversation
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load conversations.');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId?: string, schoolIdParam?: string) => {
    setLoadingMessages(true);
    setError('');
    try {
      const response = await messageService.getMessages(conversationId, schoolIdParam);
      const fetchedMessages = response.data.messages || [];
      
      // Filter out DELETED messages from display
      const visibleMessages = fetchedMessages.filter((m: Message) => {
        return m.status !== 'DELETED';
      });
      
      // Check if this is a new conversation (messages changed significantly)
      const isNewConversation = messages.length === 0 || 
        (schoolIdParam && schoolIdParam !== schoolId) ||
        (conversationId && !messages.some(m => m.conversationId === conversationId));
      
      // Update messages with fetched data
      setMessages(visibleMessages);
      
      // Reset initial load flag if this is a new conversation
      if (isNewConversation) {
        isInitialLoad.current = true;
      }
      
      // Don't set scroll flag here - let the useEffect handle it based on initial load
      
      // Mark messages as read when receiver opens the message window
      // This will prevent the sender from deleting messages once they're read
      const userIdForUnread = user?.id;
      const unreadIds = visibleMessages
        .filter(msg => {
          // Only mark messages as read if:
          // 1. Current user is the receiver
          // 2. Message is not already read
          const receiverId = typeof msg.receiverId === 'object' 
            ? (msg.receiverId as any)?._id || (msg.receiverId as any)?.id
            : msg.receiverId;
          return !msg.isRead && receiverId === userIdForUnread;
        })
        .map(msg => msg._id);
      
      if (unreadIds.length > 0) {
        try {
          await messageService.markAsRead(unreadIds);
          // Update local messages state to reflect read status
          setMessages(prevMessages => 
            prevMessages.map(msg => 
              unreadIds.includes(msg._id) 
                ? { ...msg, isRead: true, readAt: new Date().toISOString() }
                : msg
            )
          );
          // Refresh conversations to update unread count
          fetchConversations();
        } catch (err) {
          console.error('Failed to mark messages as read:', err);
        }
      }
      
      return response;
    } catch (err: any) {
      console.error('Error fetching messages:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load messages.');
      // Don't clear messages on error - keep existing ones
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    const targetSchoolId = selectedConversation?.school?._id || schoolId;
    if (!targetSchoolId) {
      setError('Please select a conversation or school');
      return;
    }

    setSending(true);
    setError('');
    const messageContent = newMessage.trim();
    try {
      const receiverId = selectedConversation?.otherUser?._id;
      await messageService.sendMessage(targetSchoolId, messageContent, receiverId);
      setNewMessage('');
      
      // Set flag to scroll to bottom after new message is sent
      shouldScrollToBottom.current = true;
      
      // Small delay to ensure message is saved before fetching
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Always use schoolId to fetch messages to ensure we get all messages
      // This ensures consistency regardless of conversationId format
      await fetchMessages(undefined, targetSchoolId);
      
      fetchConversations();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to send message.');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    try {
      await messageService.deleteMessage(messageId);
      
      // Remove message from display
      setMessages(prev => prev.filter(m => m._id !== messageId));
      
      // Refresh conversations
      fetchConversations();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to delete message.');
    }
  };

  const handleSelectConversation = (conversation: Conversation) => {
    // When selecting a conversation, we want to show all messages for that school
    // So we use the schoolId instead of conversationId to get all messages
    if (conversation.school?._id) {
      setSchoolId(conversation.school._id);
      setSelectedConversation(null);
      setSearchParams({ schoolId: conversation.school._id });
      // Reset initial load flag so it scrolls to bottom when messages load
      isInitialLoad.current = true;
      // Fetch school details for display - use slug if available, otherwise use ID
      if (conversation.school.slug) {
        fetchSchoolDetails(conversation.school.slug);
      } else {
        // If no slug, just use the school name from conversation, don't fetch
        setSchoolDetails(null);
      }
    } else {
      // Fallback to conversation-based selection if no school
      setSelectedConversation(conversation);
      setSchoolId(null);
      setSearchParams({});
      // Reset initial load flag so it scrolls to bottom when messages load
      isInitialLoad.current = true;
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Yesterday ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (days < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' }) + ' ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined }) + ' ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    }
  };

  const formatDateHeader = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return 'Today';
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'long' });
    } else {
      return date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        month: 'long', 
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  const isSameDay = (date1: Date, date2: Date) => {
    return date1.getDate() === date2.getDate() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getFullYear() === date2.getFullYear();
  };

  // Group conversations by school to show unique schools only
  // Sum up unread counts from all conversations for each school
  const uniqueSchoolConversations = conversations.reduce((acc, conv) => {
    if (!conv.school?._id) return acc;
    
    const existingIndex = acc.findIndex(c => c.school?._id === conv.school?._id);
    if (existingIndex === -1) {
      // First conversation with this school
      acc.push(conv);
    } else {
      // Merge conversations for the same school
      const existing = acc[existingIndex];
      const existingTime = existing.lastMessage?.createdAt ? new Date(existing.lastMessage.createdAt).getTime() : 0;
      const currentTime = conv.lastMessage?.createdAt ? new Date(conv.lastMessage.createdAt).getTime() : 0;
      
      // Sum up unread counts from all conversations for this school
      const totalUnreadCount = (existing.unreadCount || 0) + (conv.unreadCount || 0);
      
      // Use the conversation with the most recent message
      if (currentTime > existingTime) {
        acc[existingIndex] = {
          ...conv,
          unreadCount: totalUnreadCount
        };
      } else {
        acc[existingIndex] = {
          ...existing,
          unreadCount: totalUnreadCount
        };
      }
    }
    return acc;
  }, [] as Conversation[]);

  // Sort by last message time (most recent first) or unread count
  const sortedConversations = [...uniqueSchoolConversations].sort((a, b) => {
    // Prioritize unread messages
    if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
    if (a.unreadCount === 0 && b.unreadCount > 0) return 1;
    
    // Then sort by last message time
    const timeA = a.lastMessage?.createdAt ? new Date(a.lastMessage.createdAt).getTime() : 0;
    const timeB = b.lastMessage?.createdAt ? new Date(b.lastMessage.createdAt).getTime() : 0;
    return timeB - timeA;
  });

  const filteredConversations = sortedConversations.filter(conv => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return conv.school?.name.toLowerCase().includes(query);
  });

  if (!user) {
    return null;
  }

  return (
    <>
      <SEO
        title="Messages - Book My Yoga TTC"
        description="Communicate with yoga schools about courses and bookings."
        url="https://bookmyyogattc.com/messages"
      />
      <div className="pt-20">
      <HeroSection
        title={heroData.messages.title}
        description={heroData.messages.description}
        patternId="messagesPattern"
      />
      </div>
      <div className="min-h-screen bg-fog">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-2 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <XCircle className="h-5 w-5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
                <button
                  onClick={() => setError('')}
                  className="text-red-700 hover:text-red-900"
                >
                  <XCircle className="h-4 w-4" />
                </button>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" style={{ height: 'calc(100vh - 250px)' }}>
              {/* Conversations List */}
              <div className="lg:col-span-1 bg-white rounded-xl shadow-lg border border-ivory-200 flex flex-col overflow-hidden">
                {/* Search */}
                <div className="p-4 border-b border-ivory-200">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-ivory-400" />
                    <input
                      type="text"
                      placeholder="Search schools..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-ivory-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white text-ivory-900 placeholder-ivory-400"
                    />
                  </div>
                </div>

                {/* Conversations */}
                <div className="flex-1 overflow-y-auto">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                  ) : filteredConversations.length === 0 ? (
                    <div className="p-8 text-center">
                      <MessageSquare className="h-12 w-12 text-ivory-400 mx-auto mb-4" />
                      <p className="text-ivory-600">
                        {searchQuery ? 'No schools found' : 'No messages yet'}
                      </p>
                    </div>
                  ) : (
                    <div className="divide-y divide-ivory-200">
                      {filteredConversations.map((conversation) => {
                        const isSelected = (schoolId && conversation.school?._id === schoolId) ||
                                          (selectedConversation?.conversationId === conversation.conversationId);
                        return (
                        <button
                          key={conversation.conversationId}
                          onClick={() => handleSelectConversation(conversation)}
                          className={`w-full p-4 text-left hover:bg-ivory-50 transition-colors ${
                            isSelected
                              ? 'bg-primary/5 border-l-4 border-primary'
                              : ''
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                              {conversation.school?.logo ? (
                                <img
                                  src={conversation.school.logo}
                                  alt={conversation.school.name}
                                  className="h-12 w-12 rounded-full object-cover border-2 border-ivory-200"
                                />
                              ) : (
                                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center border-2 border-ivory-200">
                                  <Building2 className="h-6 w-6 text-primary" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <p className="text-3 font-semibold text-ivory-900 truncate">
                                  {conversation.school?.name || 'Unknown School'}
                                </p>
                                {conversation.unreadCount > 0 && (
                                  <span className="flex-shrink-0 bg-primary text-white text-1 font-semibold rounded-full h-6 w-6 flex items-center justify-center">
                                    {conversation.unreadCount}
                                  </span>
                                )}
                              </div>
                              {conversation.lastMessage && (
                                <p className="text-1 text-ivory-500 mt-1">
                                  {formatDateTime(conversation.lastMessage.createdAt)}
                                </p>
                              )}
                            </div>
                          </div>
                        </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Messages Area */}
              <div className="lg:col-span-2 bg-white rounded-xl shadow-lg border border-ivory-200 flex flex-col overflow-hidden">
                {selectedConversation || schoolId ? (
                  <>
                    {/* Messages Header */}
                    <div className="p-4 border-b border-ivory-200 flex-shrink-0">
                      <div className="flex items-center space-x-3">
                        {selectedConversation?.school?.logo || schoolDetails?.images?.logo ? (
                          <img
                            src={selectedConversation?.school?.logo || schoolDetails?.images?.logo}
                            alt={selectedConversation?.school?.name || schoolDetails?.name}
                            className="h-10 w-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <Building2 className="h-5 w-5 text-primary" />
                          </div>
                        )}
                        <div>
                          <h3 className="font-semibold text-ivory-900 text-4">
                            {selectedConversation?.school?.name || schoolDetails?.name || 'Yoga School'}
                          </h3>
                        </div>
                      </div>
                    </div>

                    {/* Messages List */}
                    <div 
                      ref={messagesContainerRef}
                      className="flex-1 overflow-y-auto p-4 min-h-0"
                      style={{ maxHeight: '100%' }}
                    >
                      {loadingMessages ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                      ) : messages.length === 0 ? (
                        <div className="text-center py-12">
                          <MessageSquare className="h-12 w-12 text-ivory-400 mx-auto mb-4" />
                          <p className="text-ivory-600">No messages yet. Start the conversation!</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {messages.map((message, index) => {
                            // Determine if current user is the sender
                            // Handle both populated object and string ID formats
                            const senderId = typeof message.senderId === 'object' 
                              ? (message.senderId as any)?._id || (message.senderId as any)?.id
                              : message.senderId;
                            
                            const userId = user?.id;
                            const isSender = senderId === userId;
                            
                            // Get sender info for display
                            const sender = typeof message.senderId === 'object' 
                              ? message.senderId 
                              : { firstName: 'User', lastName: '', email: '' };
                            
                            const messageDate = new Date(message.createdAt);
                            const prevMessageDate = index > 0 ? new Date(messages[index - 1].createdAt) : null;
                            const showDateHeader = !prevMessageDate || !isSameDay(messageDate, prevMessageDate);

                            return (
                              <div key={message._id}>
                                {/* Date Separator */}
                                {showDateHeader && (
                                  <div className="flex items-center justify-center my-6">
                                    <div className="bg-ivory-100 text-ivory-600 text-1 font-medium px-3 py-1 rounded-full">
                                      {formatDateHeader(message.createdAt)}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Message */}
                                <div className={`flex ${isSender ? 'justify-end' : 'justify-start'} mb-2`}>
                                  <div
                                    className={`max-w-[70%] rounded-lg px-4 py-2.5 ${
                                      isSender
                                        ? 'bg-primary text-white'
                                        : 'bg-ivory-100 text-ivory-900'
                                    }`}
                                  >
                                    {!isSender && (
                                      <p className="text-1 font-semibold mb-1 opacity-90">
                                        {sender.firstName} {sender.lastName}
                                      </p>
                                    )}
                                    <p className="text-2 whitespace-pre-wrap break-words leading-relaxed">
                                      {message.content}
                                    </p>
                                    <div className="flex items-center justify-between mt-2">
                                      <div className="flex items-center gap-2">
                                      </div>
                                      <div className="flex items-center gap-2">
                                        {isSender && !message.isRead && (
                                          <button
                                            onClick={() => handleDeleteMessage(message._id)}
                                            className="flex items-center gap-1 text-1 hover:opacity-80 transition-opacity px-2 py-1 rounded bg-red-500/20 text-white"
                                            title="Delete message"
                                          >
                                            <Trash2 className="h-3 w-3" />
                                            Delete
                                          </button>
                                        )}
                                        <p className={`text-1 ${isSender ? 'text-white/80' : 'text-ivory-500'}`}>
                                          {formatDateTime(message.createdAt)}
                                        </p>
                                        {isSender && message.isRead && (
                                          <span className="text-1 text-white/80" title="Read">✓✓</span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <div ref={messagesEndRef} />
                    </div>

                    {/* Message Input */}
                    <form onSubmit={handleSendMessage} className="p-4 border-t border-ivory-200 flex-shrink-0">
                      <div className="flex space-x-2">
                        <textarea
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder="Type your message..."
                          rows={2}
                          className="flex-1 px-4 py-2 border border-ivory-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none bg-white text-ivory-900 placeholder-ivory-400"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSendMessage(e);
                            }
                          }}
                        />
                        <button
                          type="submit"
                          disabled={!newMessage.trim() || sending}
                          className="px-6 py-2 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                        >
                          {sending ? (
                            <Loader2 className="h-5 w-5 animate-spin" />
                          ) : (
                            <>
                              <Send className="h-5 w-5" />
                              <span>Send</span>
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <MessageSquare className="h-16 w-16 text-ivory-400 mx-auto mb-4" />
                      <h3 className="text-5 font-semibold text-ivory-900 mb-2">Select a school</h3>
                      <p className="text-ivory-600">Choose a school from the list to view messages</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default Messages;

